import React from "react";
import { Flex } from "@fluentui/react-northstar";

import {
    CheckboxChildren,
    CheckboxTitle,
    FlexWrapperTitle,
    WrapperChildren,
} from "./index.style";

/**
 * @typedef {Object} ResourceChild
 * @property {string} id - L'identifiant unique de la ressource enfant.
 * @property {string} name - Le nom de la ressource enfant.
 * @property {boolean} checked - Indique si la ressource enfant est cochée.
 */

/**
 * @typedef {Object} Resource
 * @property {string} id - L'identifiant unique de la ressource principale.
 * @property {string} name - Le nom de la ressource principale.
 * @property {boolean} checked - Indique si la ressource principale est cochée.
 * @property {ResourceChild[]} resources - La liste des ressources enfants associées.
 */

/**
 * @typedef {Object} RessourcesCheckableProps
 * @property {Resource[]} resources - La liste des ressources principales à afficher.
 */

/**
 * RessourcesCheckable Component
 *
 * Ce composant affiche une liste de ressources principales avec leurs enfants sous forme de cases à cocher.
 *
 * @component
 * @param {RessourcesCheckableProps} props - Les propriétés du composant.
 * @returns {JSX.Element} Le composant JSX à rendre.
 *
 * @example
 * const resources = [
 *   {
 *     id: "1",
 *     name: "Ressource 1",
 *     checked: true,
 *     resources: [
 *       { id: "1.1", name: "Enfant 1.1", checked: false },
 *       { id: "1.2", name: "Enfant 1.2", checked: true },
 *     ],
 *   },
 *   {
 *     id: "2",
 *     name: "Ressource 2",
 *     checked: false,
 *     resources: [
 *       { id: "2.1", name: "Enfant 2.1", checked: false },
 *     ],
 *   },
 * ];
 *
 * <RessourcesCheckable resources={resources} />
 */
const RessourcesCheckable = ({ resources, readOnly = false }) => {
    return (
        <Flex column styles={{ marginTop: "24px" }}>
            {resources?.map((res, i) => {
                // show rows resources
                const renderChildren = res.resources.map((el, index) => {
                    return (
                        <WrapperChildren vAlign="center" key={index}>
                            <CheckboxChildren
                                label={el.name}
                                checked={el?.checked}
                                onChange={() => null}
                                data-parent={res}
                                data-children={el}
                                readOnly={readOnly}
                            />
                        </WrapperChildren>
                    );
                });
                return (
                    <Flex column key={i}>
                        <FlexWrapperTitle>
                            <CheckboxTitle
                                data={res}
                                label={res.name}
                                onChange={() => null}
                                checked={res?.checked}
                                defaultChecked={() => null}
                                readOnly={readOnly}
                            />
                        </FlexWrapperTitle>
                        {renderChildren}
                    </Flex>
                );
            })}
        </Flex>
    );
};

export default RessourcesCheckable;
